/**
 * CalendarAvailability — shared vendor/pro calendar widget with two-tone busy days:
 *
 *   - SELF blocks (vendor toggled the date themselves): always free to lift on click.
 *   - SYSTEM blocks (decline / no-response / customer-cancel-due-to-unavail): lifting
 *     costs one of N admin-configured free chances; after exhausted, lifting opens a
 *     Razorpay checkout for the admin-configured fee.
 *
 * Public dates show category icon + "AUTO" badge so the vendor knows which kind
 * they're tapping.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Calendar, Warning, Info, Lock } from '@phosphor-icons/react';
import { toast } from 'sonner';
import {
  Card, CardContent, CardDescription, CardHeader, CardTitle,
} from './ui/card';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SOURCE_LABEL = {
  pro_decline: 'You declined the offer',
  pro_no_response: 'You did not respond in time',
  vendor_decline: 'You declined a vendor offer',
  vendor_no_response: 'Multiple offers missed',
  waiter_reject: 'You rejected a waiter offer',
  waiter_timeout: 'You did not respond to a waiter offer',
  customer_cancel: 'Customer cancelled due to your unavailability',
};

const CalendarAvailability = ({ testIdPrefix = 'cal' }) => {
  const [data, setData] = useState({
    self_blocked_dates: [], system_blocked_dates: [],
    max_free_lifts: 3, lifts_used: 0, lifts_remaining: 3, pay_amount_inr: 100,
  });
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [saving, setSaving] = useState(false);
  const [confirm, setConfirm] = useState(null);  // { date, kind, source, payAmount, free }

  useEffect(() => { fetchCalendar(); }, []);

  const fetchCalendar = async () => {
    try {
      const { data: d } = await axios.get(`${BACKEND_URL}/api/vendor/calendar`, { withCredentials: true });
      setData(d);
    } catch (err) {
      console.error('Failed to fetch calendar:', err);
    }
  };

  const setSelfBlock = async (date) => {
    // Self-block via existing endpoint to keep behavior consistent.
    setSaving(true);
    try {
      const newDates = [...data.self_blocked_dates, date];
      await axios.post(`${BACKEND_URL}/api/vendor/availability`,
        { blocked_dates: newDates }, { withCredentials: true });
      await fetchCalendar();
      toast.success('Date blocked');
    } catch {
      toast.error('Failed to block date');
    }
    setSaving(false);
  };

  const tryUnblock = async (date) => {
    setSaving(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/vendor/calendar/unblock`,
        { date }, { withCredentials: true });
      const r = res.data;
      await fetchCalendar();
      if (r.kind === 'self') toast.success('Date unblocked');
      else toast.success(`Auto-busy lifted — ${r.lifts_remaining} free lift(s) remaining`);
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 402 && detail && typeof detail === 'object' && detail.code === 'payment_required') {
        // Open paid lift modal
        setConfirm({ date, kind: 'paid', payAmount: detail.pay_amount_inr });
      } else {
        toast.error(detail?.message || detail || 'Failed to lift date');
      }
    }
    setSaving(false);
  };

  const payAndLift = async () => {
    if (!confirm || confirm.kind !== 'paid') return;
    setSaving(true);
    try {
      // 1. Create order
      const orderRes = await axios.post(`${BACKEND_URL}/api/vendor/calendar/lift/create-order`,
        { date: confirm.date }, { withCredentials: true });
      const order = orderRes.data;

      // 2. Open Razorpay checkout
      if (!window.Razorpay) {
        toast.error('Payment SDK not loaded — please refresh the page');
        setSaving(false);
        return;
      }
      const rzp = new window.Razorpay({
        key: order.razorpay_key_id,
        amount: order.amount_paise,
        currency: order.currency,
        name: 'BeeWeddy',
        description: `Lift busy day ${confirm.date}`,
        order_id: order.razorpay_order_id,
        handler: async (resp) => {
          try {
            await axios.post(`${BACKEND_URL}/api/vendor/calendar/lift/verify-payment`, {
              date: confirm.date,
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
            }, { withCredentials: true });
            toast.success('Paid · Busy day lifted');
            setConfirm(null);
            await fetchCalendar();
          } catch (e) {
            toast.error(e?.response?.data?.detail || 'Verification failed');
          }
          setSaving(false);
        },
        modal: { ondismiss: () => setSaving(false) },
        theme: { color: '#B85440' },
      });
      rzp.open();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to create order');
      setSaving(false);
    }
  };

  const onDayClick = (dateStr) => {
    const isSelf = data.self_blocked_dates.includes(dateStr);
    const sysItem = data.system_blocked_dates.find((d) => d.date === dateStr);
    if (isSelf) {
      // Free unblock (still go through unblock endpoint for consistency)
      tryUnblock(dateStr);
    } else if (sysItem) {
      // System block — ask confirmation first (so vendor knows it costs a chance)
      setConfirm({
        date: dateStr, kind: 'system', source: sysItem.source,
        free: data.lifts_remaining > 0,
        payAmount: data.pay_amount_inr,
      });
    } else {
      // Free date — block it
      setSelfBlock(dateStr);
    }
  };

  const confirmLift = async () => {
    if (!confirm) return;
    setConfirm(null);
    await tryUnblock(confirm.date);
  };

  const getDays = (date) => {
    const year = date.getFullYear(); const month = date.getMonth();
    return {
      firstDay: new Date(year, month, 1).getDay(),
      daysInMonth: new Date(year, month + 1, 0).getDate(),
      year, month,
    };
  };
  const { firstDay, daysInMonth, year, month } = getDays(currentMonth);
  const monthName = currentMonth.toLocaleString('default', { month: 'long', year: 'numeric' });
  const today = new Date().toISOString().split('T')[0];

  return (
    <>
      <Card className="bg-white border-[#E8E1D5] mt-6" data-testid={`${testIdPrefix}-calendar-availability`}>
        <CardHeader>
          <CardTitle className="font-heading text-xl flex items-center gap-2">
            <Calendar size={20} className="text-brand-primary" /> Calendar Availability
          </CardTitle>
          <CardDescription>
            Tap a date to mark yourself busy. Red dates are self-blocked (free to lift).
            Amber dates were auto-blocked because you declined or didn't respond — you have
            <span className="font-bold text-amber-700"> {data.lifts_remaining} </span>
            free lift(s) left, then ₹{data.pay_amount_inr} per lift.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between mb-4">
            <button type="button"
              className="px-3 py-1 border border-[#E8E1D5] text-sm font-bold hover:bg-brand-primary/5 transition-colors"
              onClick={() => setCurrentMonth(new Date(year, month - 1, 1))}
              data-testid={`${testIdPrefix}-prev-month`}>Prev</button>
            <span className="font-heading text-lg font-medium">{monthName}</span>
            <button type="button"
              className="px-3 py-1 border border-[#E8E1D5] text-sm font-bold hover:bg-brand-primary/5 transition-colors"
              onClick={() => setCurrentMonth(new Date(year, month + 1, 1))}
              data-testid={`${testIdPrefix}-next-month`}>Next</button>
          </div>

          <div className="grid grid-cols-7 gap-1 mb-1">
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(d => (
              <div key={d} className="text-center text-xs font-bold text-text-secondary py-1">{d}</div>
            ))}
          </div>

          <div className="grid grid-cols-7 gap-1">
            {Array.from({ length: firstDay }).map((_, i) => (<div key={`empty-${i}`} className="h-10" />))}
            {Array.from({ length: daysInMonth }).map((_, i) => {
              const day = i + 1;
              const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
              const isSelf = data.self_blocked_dates.includes(dateStr);
              const isSystem = data.system_blocked_dates.some((d) => d.date === dateStr);
              const isPast = dateStr < today;
              let cls = 'h-10 text-sm font-medium rounded transition-all relative';
              if (isPast) cls += ' text-gray-300 cursor-not-allowed';
              else if (isSystem) cls += ' bg-amber-500 text-white hover:bg-amber-600';
              else if (isSelf) cls += ' bg-red-500 text-white hover:bg-red-600';
              else if (dateStr === today) cls += ' bg-brand-primary/10 text-brand-primary border border-brand-primary hover:bg-brand-primary/20';
              else cls += ' hover:bg-brand-primary/5 text-text-primary border border-transparent';
              return (
                <button key={day} type="button" disabled={isPast || saving}
                  onClick={() => onDayClick(dateStr)}
                  className={cls}
                  data-testid={`${testIdPrefix}-day-${dateStr}`}>
                  {day}
                  {isSystem && <span className="absolute top-0 right-0 text-[8px] leading-none px-0.5 bg-white/30 rounded-bl">A</span>}
                </button>
              );
            })}
          </div>

          <div className="flex flex-wrap items-center gap-4 mt-4 pt-3 border-t border-[#E8E1D5]">
            <div className="flex items-center gap-1.5 text-xs">
              <div className="w-4 h-4 bg-red-500 rounded" /> <span className="text-text-secondary">Self-blocked (free lift)</span>
            </div>
            <div className="flex items-center gap-1.5 text-xs">
              <div className="w-4 h-4 bg-amber-500 rounded" /> <span className="text-text-secondary">Auto-blocked (system)</span>
            </div>
            <div className="flex items-center gap-1.5 text-xs">
              <div className="w-4 h-4 border border-brand-primary bg-brand-primary/10 rounded" /> <span className="text-text-secondary">Today</span>
            </div>
            {saving && <span className="text-xs text-brand-primary font-medium ml-auto">Saving...</span>}
          </div>

          {(data.self_blocked_dates.length + data.system_blocked_dates.length) > 0 && (
            <p className="text-xs text-text-secondary mt-2">
              {data.self_blocked_dates.length} self · {data.system_blocked_dates.length} auto · {data.lifts_remaining}/{data.max_free_lifts} free lifts left
            </p>
          )}
        </CardContent>
      </Card>

      {/* Confirmation / payment modal */}
      {confirm && (
        <div className="fixed inset-0 bg-black/60 z-[100] flex items-center justify-center p-4" data-testid="cal-lift-modal">
          <div className="bg-white rounded-lg max-w-sm w-full p-6 border-2 border-amber-500">
            <div className="flex items-center gap-2 mb-2">
              {confirm.free !== false ? <Warning size={22} className="text-amber-600" /> : <Lock size={22} className="text-red-600" />}
              <h3 className="font-heading text-xl">
                {confirm.kind === 'paid' ? 'Payment Required' : 'Lift Auto-Busy Day?'}
              </h3>
            </div>
            <p className="text-sm text-text-secondary mb-3">
              <span className="font-bold text-text-primary">{confirm.date}</span> was auto-marked busy because:
            </p>
            <p className="text-sm bg-amber-50 border border-amber-200 px-3 py-2 rounded mb-4 flex items-start gap-2">
              <Info size={16} className="text-amber-700 mt-0.5 shrink-0" />
              <span>{SOURCE_LABEL[confirm.source] || 'Order was not honoured'}</span>
            </p>

            {confirm.kind === 'paid' || !confirm.free ? (
              <div className="bg-red-50 border border-red-200 px-3 py-3 rounded mb-4">
                <p className="text-sm font-bold text-red-700 mb-1">
                  No free lift chances remaining
                </p>
                <p className="text-sm text-red-700">
                  Pay <span className="font-bold">₹{confirm.payAmount}</span> to lift this busy day.
                </p>
              </div>
            ) : (
              <div className="bg-blue-50 border border-blue-200 px-3 py-3 rounded mb-4 text-sm text-blue-700">
                This will use <span className="font-bold">1 of your {data.lifts_remaining}</span> free lift chances.
              </div>
            )}

            <div className="flex gap-2">
              <button type="button"
                onClick={() => setConfirm(null)}
                className="flex-1 py-2 border border-[#E8E1D5] rounded text-sm font-bold hover:bg-gray-50"
                data-testid="cal-lift-cancel">
                Cancel
              </button>
              {(confirm.kind === 'paid' || !confirm.free) ? (
                <button type="button"
                  onClick={payAndLift} disabled={saving}
                  className="flex-1 py-2 bg-brand-primary text-sand-50 rounded text-sm font-bold hover:bg-[#A84A38] disabled:opacity-50"
                  data-testid="cal-lift-pay">
                  Pay ₹{confirm.payAmount}
                </button>
              ) : (
                <button type="button"
                  onClick={confirmLift} disabled={saving}
                  className="flex-1 py-2 bg-amber-600 text-white rounded text-sm font-bold hover:bg-amber-700 disabled:opacity-50"
                  data-testid="cal-lift-confirm">
                  Use Free Lift
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CalendarAvailability;
